// These helpers are calling FTW's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import { types as sdkTypes, transit } from './sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';
import CRM, { type DirectoryBooking } from '@/crm/CRM';
import Try from './try';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: (v) => new sdkTypes.BigDecimal(v.toString()),
    reader: (v) => new Decimal(v.value),
  },
];

const serialize = (data) => {
  return transit.write(data, {
    typeHandlers,
    verbose: config.sdk.transitVerbose,
  });
};

const deserialize = (str: string): any => {
  return transit.read(str, { typeHandlers });
};

const post = (path: string, body?: any): Promise<any> => {
  const url = `${apiBaseUrl()}${path}`;
  const options: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window.fetch(url, options).then((res) => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader
      ? contentTypeHeader.split(';')[0]
      : null;

    if (res.status >= 400) {
      return res.json().then((data) => {
        let e = new Error();
        e.message = data.message || data.error || 'API Error';
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const get = (path: string): Promise<any> => {
  const url = `${apiBaseUrl()}${path}`;
  const options: RequestInit = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
  };
  return window.fetch(url, options).then((res) => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader
      ? contentTypeHeader.split(';')[0]
      : null;

    if (res.status >= 400) {
      return res.json().then((data) => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = (body) => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the booking data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = (body) => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = (body) => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Flex with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = (body) => {
  return post('/api/auth/create-user-with-idp', body);
};

export const videoConferenceBooking = (body) => {
  return post('/api/video-service-booking', body);
};

export const videoConferenceGetBookings = () => {
  return get('/api/video-service-get-bookings');
};

export type VideoConferenceDirectoryBooking = {
  information: string;
  reason: string;
  preDialPhoneNumber: string;
  organisation: {
    id: string;
    name: string;
  };
  departmentName: string;
  email: string;
  userName: string;
};
export const videoConferenceDirectoryBooking = async (
  body: VideoConferenceDirectoryBooking
) => {
  const response = await post('/api/video-service-directory-booking', body);

  const [error] = await Try(
    CRM.recordDirectoryBooking({
      customerName: body.userName,
      customerEmail: body.email,
      organisationName: body.organisation.name,
      departmentName: body.departmentName,
      phoneNumber: body.preDialPhoneNumber,
      appointmentDate: new Date().toISOString().split('T')[0],
      appointmentTime: new Date().toISOString().split('T')[1].split('.')[0],
      description: `This is a video call for user to ${body.reason}`,
      requestedDeafUserName: body.userName,
    })
  );

  if (error) {
    console.error('Error recording directory booking:', error);
  }

  return response;
};

export const queryAllUsers = () => {
  return get('/api/all-users');
};


export const videoDirectoryOdoo = (body: DirectoryBooking) => {
  return post('/api/video-directory-odoo', body);
};

export const saveDoc = (body) => {
  const reader = new FileReader();
  reader.readAsDataURL(body.file); // `.file` is a File which subclasses Blob

  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      const base64File = reader.result;
      body.file = base64File;

      post('/api/save-doc', body).then(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        }
      );
    };
  });
};


export const postLogin = body => {
  return post('/api/login', body);
};

export const postLogout = () => {
  return post('/api/logout', {});
};

export const getAuthInfo = () => {
  return get('/api/auth-info');
}